import { isTelemetryReady, trackEvent as coreTrackEvent, trackPageView as coreTrackPageView, trackException as coreTrackException } from "./ai";
import type { ConsentState } from "@/consent/useConsent";

type TelemetryProperties = Record<string, unknown>;

let consentState: ConsentState | null = null;

// Debug flag: in dev, you can skip consent checks for testing (should be false in prod)
const SKIP_CONSENT_DEBUG = import.meta.env.DEV && import.meta.env.VITE_TELEMETRY_SKIP_CONSENT === "true";

// Log once if debug flag is active
if (SKIP_CONSENT_DEBUG) {
  console.warn(
    "[Telemetry] ⚠️ VITE_TELEMETRY_SKIP_CONSENT is ON – consent checks are disabled for debugging. " +
      "This should NEVER be enabled in production or staging.",
  );
}

/**
 * Register the current consent state.
 * Called whenever user's consent preferences change.
 */
export function registerConsentState(state: ConsentState): void {
  consentState = state;
}

/**
 * Check if analytics tracking is allowed under current consent.
 */
function isAnalyticsAllowed(): boolean {
  if (SKIP_CONSENT_DEBUG) return true;
  if (!consentState) return false;
  return consentState.isAllowed("analytics");
}

/**
 * Track an event only if analytics consent is given and telemetry is ready.
 */
export function trackEventWithConsent(name: string, properties?: TelemetryProperties): void {
  if (!isAnalyticsAllowed() || !isTelemetryReady()) {
    return;
  }
  coreTrackEvent(name, properties);
}

/**
 * Track a page view only if analytics consent is given and telemetry is ready.
 */
export function trackPageViewWithConsent(name: string, properties?: TelemetryProperties): void {
  if (!isAnalyticsAllowed() || !isTelemetryReady()) {
    return;
  }
  coreTrackPageView(name, properties);
}

/**
 * Track an exception only if analytics consent is given and telemetry is ready.
 */
export function trackExceptionWithConsent(error: Error, properties?: TelemetryProperties): void {
  if (!isAnalyticsAllowed() || !isTelemetryReady()) {
    return;
  }
  coreTrackException(error, properties);
}

/**
 * Get current consent state (for debugging/monitoring).
 */
export function getConsentState(): ConsentState | null {
  return consentState;
}
